import { type ReactElement, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import { type Permission } from '@irl/types';

import { AccessDeniedScreen } from '@/components/auth/access-denied-screen';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionState } from '@/hooks/usePermissions';

interface ProtectedRouteProps {
  permission: Permission;
  chatId?: string;
  children: ReactNode;
}

export function ProtectedRoute({
  permission,
  chatId,
  children,
}: ProtectedRouteProps): ReactElement {
  const { allowed, isLoading } = usePermissionState(permission, chatId);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!allowed) {
    return <AccessDeniedScreen />;
  }

  return <>{children}</>;
}

interface ChatProtectedRouteProps {
  permission: Permission;
  children: ReactNode;
}

/** Route guard scoped to `:id` chat param from the URL. */
export function ChatProtectedRoute({
  permission,
  children,
}: ChatProtectedRouteProps): ReactElement {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <AccessDeniedScreen />;
  }

  return (
    <ProtectedRoute
      permission={permission}
      chatId={id}
    >
      {children}
    </ProtectedRoute>
  );
}
